import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Flag as FlagIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission, hasReadPermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";
import { useHistory } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';

const InquiryStatus = () => {
  const history = useHistory();
  const canWrite = hasWritePermission('master.general-master') || hasWritePermission('general-master') || hasWritePermission('inquiry-status');
  const canRead = hasReadPermission('master.general-master') || hasReadPermission('general-master') || hasReadPermission('inquiry-status');
  const [inquiryStatusData, setInquiryStatusData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageTypeFilter, setStageTypeFilter] = useState('');
  const [inquiryCodeFilter, setInquiryCodeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedInquiryStatusForDelete, setSelectedInquiryStatusForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingInquiryStatus, setEditingInquiryStatus] = useState(null);
  const [newInquiryStatus, setNewInquiryStatus] = useState({
    stageType: '',
    inquiryCode: '',
    statusName: '',
    colorCode: '#000000',
    status: true,
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Stage Type options
  const stageTypeOptions = ['Lead', 'Visit', 'Booking'];

  // Inquiry Code options
  const inquiryCodeOptions = ['Interested', 'Less_Interested', 'Very_Interested'];

  // Fetch inquiry statuses
  const fetchInquiryStatuses = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=inquiryStatus`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const inquiryStatuses = Array.isArray(result.data)
        ? result.data.map((item) => ({
            id: item._id,
            inquiryStatusId: item.details?.inquiryStatusId || item._id.substring(0, 8).toUpperCase(),
            stageType: item.details?.stageType || '',
            inquiryCode: item.details?.inquiryCode || '',
            statusName: item.details?.statusName || 'Unknown',
            colorCode: item.details?.colorCode || '#000000',
            status: item.status === true ? 'Active' : 'Inactive',
            rawStatus: item.status,
          }))
        : [];

      setInquiryStatusData(inquiryStatuses);
    } catch (err) {
      setApiError('Failed to load inquiry statuses.');
      setInquiryStatusData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiryStatuses();
  }, []);

  // Extract unique values for filters
  const stageTypeOptionsFromData = [...new Set(inquiryStatusData.map((s) => s.stageType))]
    .filter(Boolean)
    .sort();

  const inquiryCodeOptionsFromData = [...new Set(inquiryStatusData.map((s) => s.inquiryCode))]
    .filter(Boolean)
    .sort();

  // Filter logic
  const getFilteredInquiryStatuses = () => {
    return inquiryStatusData.filter((s) => {
      const matchesSearch =
        s.statusName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.stageType.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.inquiryCode.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStageType = !stageTypeFilter || s.stageType === stageTypeFilter;
      const matchesInquiryCode = !inquiryCodeFilter || s.inquiryCode === inquiryCodeFilter;
      const matchesStatus = !statusFilter || s.status === statusFilter;
      return matchesSearch && matchesStageType && matchesInquiryCode && matchesStatus;
    });
  };

  const filteredInquiryStatuses = getFilteredInquiryStatuses();

  // Pagination
  const paginatedInquiryStatuses = filteredInquiryStatuses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newInquiryStatus.stageType.trim()) err.stageType = 'Stage Type is required';
    if (!newInquiryStatus.inquiryCode.trim()) err.inquiryCode = 'Inquiry Code is required';
    if (!newInquiryStatus.statusName.trim()) err.statusName = 'Status Name is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return inquiryStatusData.some(
      (s) =>
        s.statusName.toLowerCase() === newInquiryStatus.statusName.trim().toLowerCase() &&
        s.stageType === newInquiryStatus.stageType &&
        s.inquiryCode === newInquiryStatus.inquiryCode &&
        s.id !== editingInquiryStatus?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingInquiryStatus(null);
    setNewInquiryStatus({
      stageType: '',
      inquiryCode: '',
      statusName: '',
      colorCode: '#000000',
      status: true,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingInquiryStatus(null);
    setNewInquiryStatus({
      stageType: '',
      inquiryCode: '',
      statusName: '',
      colorCode: '#000000',
      status: true,
    });
    setErrors({});
  };

  const handleEdit = (inquiryStatus) => {
    setEditingInquiryStatus(inquiryStatus);
    setNewInquiryStatus({
      stageType: inquiryStatus.stageType,
      inquiryCode: inquiryStatus.inquiryCode,
      statusName: inquiryStatus.statusName,
      colorCode: inquiryStatus.colorCode,
      status: inquiryStatus.rawStatus,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (inquiryStatus) => {
    setSelectedInquiryStatusForDelete(inquiryStatus);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedInquiryStatusForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails/${selectedInquiryStatusForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error('Delete failed');

      setAlertConfig({ title: 'Success', message: 'Inquiry status deleted successfully!' });
      setAlertOpen(true);
      fetchInquiryStatuses(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete inquiry status.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedInquiryStatusForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ statusName: 'Inquiry Status already exists with this combination' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'inquiryStatus',
        details: {
          stageType: newInquiryStatus.stageType,
          inquiryCode: newInquiryStatus.inquiryCode,
          statusName: newInquiryStatus.statusName.trim(),
          colorCode: newInquiryStatus.colorCode || '#000000',
        },
        status: newInquiryStatus.status,
      };

      let response;
      if (editingInquiryStatus) {
        response = await fetch(`${API_BASE}/api/globalMasterDetails/${editingInquiryStatus.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE}/api/globalMasterDetails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) throw new Error('Save failed');

      setAlertConfig({
        title: 'Success',
        message: editingInquiryStatus ? 'Inquiry status updated successfully!' : 'Inquiry status added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchInquiryStatuses(true);
    } catch (err) {
      setErrors({ statusName: 'Failed to save inquiry status.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStageTypeFilter('');
    setInquiryCodeFilter('');
    setStatusFilter('');
    setPage(0);
    fetchInquiryStatuses(true);
  };

  const handleChange = (field, value) => {
    setNewInquiryStatus((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Inquiry Status Master. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{ 
              backgroundColor: '#536DFE', 
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none' 
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
       <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Inquiry Status Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchInquiryStatuses()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
            >
              Add Inquiry Status
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the inquiry status <strong>{selectedInquiryStatusForDelete?.statusName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchInquiryStatuses()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search Inquiry Status..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Stage Type</InputLabel>
                <Select
                  value={stageTypeFilter}
                  onChange={(e) => {
                    setStageTypeFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Stage Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {stageTypeOptionsFromData.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Inquiry Code</InputLabel>
                <Select
                  value={inquiryCodeFilter}
                  onChange={(e) => {
                    setInquiryCodeFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Inquiry Code"
                >
                  <MenuItem value="">All Codes</MenuItem>
                  {inquiryCodeOptionsFromData.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredInquiryStatuses.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} inquiry statuses matching your criteria.` : 'No inquiry statuses found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !inquiryStatusData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Stage Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Inquiry Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Color Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInquiryStatuses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No inquiry statuses found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInquiryStatuses.map((inquiryStatus, index) => (
                    <TableRow key={inquiryStatus.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{inquiryStatus.stageType}</TableCell>
                      <TableCell>{inquiryStatus.inquiryCode}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: inquiryStatus.colorCode || '#000000',
                              border: '1px solid #ccc',
                            }}
                          />
                          {inquiryStatus.statusName}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              backgroundColor: inquiryStatus.colorCode || '#000000',
                              border: '1px solid #ccc',
                            }}
                          />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {inquiryStatus.colorCode || '#000000'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inquiryStatus.status}
                          color={inquiryStatus.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite ? (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(inquiryStatus)}
                                disabled={isLoading}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(inquiryStatus)}
                                disabled={isLoading}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <></>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredInquiryStatuses.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingInquiryStatus ? 'Edit Inquiry Status' : 'Add New Inquiry Status'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!errors.stageType}>
                  <InputLabel>Stage Type</InputLabel>
                  <Select
                    value={newInquiryStatus.stageType}
                    onChange={(e) => handleChange('stageType', e.target.value)}
                    label="Stage Type"
                  >
                    {stageTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.stageType && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.stageType}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!errors.inquiryCode}>
                  <InputLabel>Inquiry Code</InputLabel>
                  <Select
                    value={newInquiryStatus.inquiryCode}
                    onChange={(e) => handleChange('inquiryCode', e.target.value)}
                    label="Inquiry Code"
                  >
                    {inquiryCodeOptions.map((code) => (
                      <MenuItem key={code} value={code}>
                        {code}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.inquiryCode && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.inquiryCode}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Status Name"
                  value={newInquiryStatus.statusName}
                  onChange={(e) => handleChange('statusName', e.target.value)}
                  error={!!errors.statusName}
                  helperText={errors.statusName || 'e.g., Hot, Cold, Medium, etc.'}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Color Code"
                    type="color"
                    value={newInquiryStatus.colorCode || '#000000'}
                    onChange={(e) => handleChange('colorCode', e.target.value)}
                    sx={{
                      '& input[type="color"]': {
                        width: '100%',
                        height: 56,
                        cursor: 'pointer',
                        border: 'none',
                        borderRadius: 1,
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Hex Code"
                    value={newInquiryStatus.colorCode || '#000000'}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                        handleChange('colorCode', value || '#000000');
                      }
                    }}
                    placeholder="#000000"
                    inputProps={{ maxLength: 7 }}
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Default: Black (#000000)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newInquiryStatus.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingInquiryStatus ? 'Update' : 'Add'} Inquiry Status
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default InquiryStatus;
